import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { useCardOverlay } from '../../hooks/useCardOverlay';
import { useUrlManager } from '../../hooks/useUrlManager';
import CardCompany from '../CardCompany/CardCompany';
import styles from './CardOverlay.module.css';

const CardOverlay = ({ cardId, onClose }) => {
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const location = useLocation();
    const originalPath = useRef(location.pathname);

    const { data, isLoading, error } = useCardOverlay(cardId);
    
    const urlManagerData = data?.companyname ? {
        companyname: data.companyname
    } : null;
    
    useUrlManager(urlManagerData);

    // Memoizamos handleResize con useCallback
    const handleResize = useCallback(() => {
        setWindowWidth(window.innerWidth);
    }, []);

    // Memoizamos la función de restauración de URL
    const restoreUrl = useCallback(() => {
        const path = originalPath.current;
        if (path?.startsWith('/test/')) {
            window.history.replaceState(null, '', path);
        }
    }, []); // No hay dependencias porque usamos ref

    useEffect(() => {
        window.addEventListener('resize', handleResize);
        document.body.style.overflow = 'hidden';
        
        return () => {
            window.removeEventListener('resize', handleResize);
            document.body.style.overflow = 'auto';
            restoreUrl();
        };
    }, [handleResize, restoreUrl]);

    const handleClose = (e) => {
        e?.preventDefault();
        e?.stopPropagation();
        
        restoreUrl();
        onClose?.();
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            handleClose(e);
        }
    };

    const getOverlaySize = () => {
        if (windowWidth >= 1440) return 'Desktop 1440';
        if (windowWidth >= 1200) return 'Desktop 1200';
        if (windowWidth >= 810) return 'Tablet';
        return 'Phone';
    };

    if (error) {
        console.error('CardOverlay Error:', error);
        return null;
    }

    if (isLoading) {
        return null;
    }

    return (
        <div 
            className={styles.overlay}
            onClick={handleOverlayClick}
        >
            <AnimatePresence>
                <motion.div
                    className={styles.content}
                    initial={{ y: '100%' }}
                    animate={{ y: 0 }}
                    exit={{ y: '100%' }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                >
                    <CardCompany
                        companyId={cardId}
                        size={getOverlaySize()}
                        onClose={handleClose}
                    />
                </motion.div>
            </AnimatePresence>
        </div>
    );
};

export default CardOverlay;